import useResumoCompraHook from '@/components/organisms/ResumoCompra/hook/useResumoCompraHook';
import { useSacola } from '@/hooks/useSacola';
import { useSnackbar } from '@/hooks/useSnackbar';
import { registrarEvento } from '@/services/googleAnalytics';
import {
  alterarFormaPagamentoPedido,
  realizarNovoPedido,
} from '@/services/pagamentos/pagamentoService';
import {
  construirParametrosParaRequisicaoNovoPedido,
  recuperarCombosIDs,
  recuperarProdutosFixosCombo,
} from '@/services/pagamentos/reducers';
import { APIResponseNovoPedido } from '@/services/pagamentos/types/pagamento';
import { TipoPagamentoID } from '@/services/pagamentos/types/tipoPagamentoEnum';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

export const KEY_VALOR_PEDIDO_ID_FALHA = 'pedidoIDComFalha';

export function removerValorPedidoIDComFalha() {
  return sessionStorage.removeItem(`chippuPlace@${KEY_VALOR_PEDIDO_ID_FALHA}`);
}

export default function useRevisaoPageHook() {
  const router = useRouter();
  const { itens, formaDePagamento, limparSacola, formatarFormaPagamento } =
    useSacola();

  const { mostrarSnackbar } = useSnackbar();
  const { valoresResumoCompra } = useResumoCompraHook();

  const [realizandoNovoPedido, setRealizandoNovoPedido] = useState(false);

  function verificarSucessoRequisicao(
    requisicao: APIResponseNovoPedido,
  ): boolean {
    if (!requisicao.sucesso) {
      mostrarSnackbar({
        color: 'error',
        highContrast: true,
        mensagem: requisicao.mensagem ?? '',
        size: 1,
        variant: 'solid',
      });

      if (requisicao?.dados?.pedidoID) {
        sessionStorage.setItem(
          `chippuPlace@${KEY_VALOR_PEDIDO_ID_FALHA}`,
          requisicao.dados.pedidoID,
        );

        formatarFormaPagamento();
        router.back();
      }
      return false;
    }
    return true;
  }

  async function realizarCompra() {
    try {
      setRealizandoNovoPedido(true);

      const usuarioPossuiUmPedidoComFalha = sessionStorage.getItem(
        `chippuPlace@${KEY_VALOR_PEDIDO_ID_FALHA}`,
      );

      if (usuarioPossuiUmPedidoComFalha) {
        const retornoRecuperarPedidoComFalha =
          await alterarFormaPagamentoPedido({
            pedidoID: usuarioPossuiUmPedidoComFalha,
            tipoPagamentoID: TipoPagamentoID.cartao,
            ...formaDePagamento?.cartao,
          });

        if (!verificarSucessoRequisicao(retornoRecuperarPedidoComFalha)) {
          return;
        }

        removerValorPedidoIDComFalha();
      } else {
        const retornoRealizacaoNovoPedido = await realizarNovoPedido({
          planoID: construirParametrosParaRequisicaoNovoPedido({
            sacola: itens,
            campo: 'opcaoSelecionadaProduto',
          }),
          comboPlanoID: [
            ...construirParametrosParaRequisicaoNovoPedido({
              sacola: itens,
              campo: 'opcaoSelecionadaPlano',
            }),
            ...recuperarProdutosFixosCombo(itens),
          ],
          comboID: recuperarCombosIDs(itens),
          tipoPagamentoID: TipoPagamentoID.cartao,
          ...formaDePagamento?.cartao,
        });

        if (!verificarSucessoRequisicao(retornoRealizacaoNovoPedido)) {
          return;
        }
      }

      registrarEvento('purchase', {
        currency: 'BRL',
        value: valoresResumoCompra?.valorTotal,
        items: itens.map((itemSacola) => {
          if (itemSacola?.produto && itemSacola?.opcaoSelecionadaProduto) {
            return {
              item_id: itemSacola?.opcaoSelecionadaProduto?.id,
              item_brand: itemSacola?.produto?.nome,
              item_name: itemSacola?.opcaoSelecionadaProduto?.nome,
              discount: itemSacola?.opcaoSelecionadaProduto?.desconto,
              quantity: itemSacola?.quantidade,
            };
          } else if (itemSacola?.plano) {
            return {
              item_id: itemSacola.plano.comboID,
              item_name: itemSacola.plano.nome,
              discount: itemSacola.plano.desconto,
            };
          }
        }),
      });

      limparSacola();
      formatarFormaPagamento();

      router.push('/pagamento-cartao', { scroll: true });
    } catch (error) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setRealizandoNovoPedido(false);
    }
  }

  function alterarFormaPagamento() {
    router.back();
  }

  return {
    itens,
    formaDePagamento,
    realizarCompra,
    realizandoNovoPedido,
    alterarFormaPagamento,
  };
}
